import React, { useState, useEffect } from "react";
import ReactJson from "react-json-view";
import _ from "lodash";
import { useDebouncedValue } from "@mantine/hooks";

import {
  Box,
  Checkbox,
  Table,
  NumberInput,
  Button,
  ActionIcon,
  Select,
  Text,
  TextInput,
  Group,
} from "@mantine/core";
import RemoteSelect2 from "./remoteSelect2";
import { IconTrash } from "@tabler/icons";
import {
  DATA_MARIAGE_STATUS,
  DATA_OVERRIDING_LEVEL_TYPE,
} from "../data/options";
import { useFormatter } from "../hooks/useFomatter";
import { useTranslation } from "react-i18next";
import FieldLabelGuide from "./fieldLabelGuide";
import {
  overridingNumberingKey,
  renumberOverridingRows,
} from "./renumberOverridingRows";

const OverridingTable = ({
  form: mainForm,
  name,
  table,
  isSpecial = false,
  specialTableIndex,
  size = "md",
}) => {
  const rowsPath = !isSpecial
    ? name
    : `specialTables.${specialTableIndex}.rows`;
  const storedRows = _.get(mainForm.values, rowsPath);
  const overrideTable = Array.isArray(storedRows) ? storedRows : [];
  const displayRows = renumberOverridingRows(overrideTable);

  useEffect(() => {
    if (!Array.isArray(storedRows)) return;
    const next = renumberOverridingRows(storedRows);
    if (overridingNumberingKey(storedRows) === overridingNumberingKey(next)) return;
    mainForm.setFieldValue(rowsPath, next);
  }, [storedRows, rowsPath, mainForm]);

  const [formatter] = useFormatter();
  const { t } = useTranslation();
  const allowSkipProviderRate = _.get(mainForm.values, "allowSkipProviderRate");
  const head = (
    <tr>
      <th>
        <Text size={size}>#</Text>
      </th>
      <th>
        <Text size={size}>{t("Level")}</Text>
      </th>
      <th>
        <Text size={size}>{t("Level Type")}</Text>
      </th>
      <th>
        <Text size={size}>{t("Percentage")}</Text>
      </th>
      <th>
        <Text size={size}>{t("Remain")}</Text>
      </th>
      <th>
        <Text size={size}>{t("Actions")}</Text>
      </th>
    </tr>
  );

  const OverrideUserLevelCell = ({ row }) => {
    if (row.level == 0) return "Producer";

    switch (row.levelType) {
      case "upper":
        return "Upper " + row.level;
      case "topUpline":
        return "Top Upline";
      case "user":
        return (
          <RemoteSelect2
            size={"xs"}
            apiEntity="user"
            labelField="name"
            labelRender={(data) =>
              `[${data.staffId}] ${data.name ?? ""} ${data.cname ?? ""} ${
                data.aliasName ?? ""
              }`
            }
            searchFields={["name", "staffId", "aliasName"]}
            valueField="_id"
            value={row.user}
            onDataChange={(d) => {
              console.log(d);
              row.user = d?._id ?? null;
              updateFormValue();
            }}
          />
        );
      case "userGroup":
        return (
          <RemoteSelect2
            size={"xs"}
            apiEntity="userGroup"
            labelField="name"
            valueField="_id"
            value={row.userGroup}
            onDataChange={(d) => {
              // console.log(d);
              row.userGroup = d?._id ?? null;
              updateFormValue();
            }}
          />
        );
      default:
        return "Upper " + row.level;
    }
  };

  const hasRemainLevel = () => {
    let index = overrideTable.findIndex((r) => r.isRemainLevel);
    return index != -1;
  };

  const OverrideLevelRow = ({ key, row, size = "xs" }) => {
    return (
      <tr key={key}>
        <td>
          <Text size={size}>{row.index}</Text>
        </td>
        <td width={400}>
          <OverrideUserLevelCell row={row} />
        </td>
        <td width={200}>
          {row.level == 0 ? (
            ""
          ) : (
            <Select
              size={size}
              data={DATA_OVERRIDING_LEVEL_TYPE}
              defaultValue={row.levelType}
              width="10px"
              onChange={(v) => {
                row.levelType = v;
                if (v === "topUpline") row.level = -3;
                else if (v === "userGroup") row.level = -1;
                else if (v === "user") row.level = -2;
                updateFormValue();
              }}
            />
          )}
        </td>

        <td>
          <NumberInput
            size="xs"
            value={row.percentage}
            precision={3}
            noClampOnBlur
            onBlur={(e) => {
              row.percentage = Number(e.target.value);
              updateFormValue();
            }}
            miw={100}
          />
        </td>
        <td>
          <Checkbox
            size="xs"
            checked={row.isRemainLevel}
            onChange={() => {
              row.isRemainLevel = !row.isRemainLevel;
              updateFormValue();
            }}
            disabled={hasRemainLevel() && !row.isRemainLevel}
          />
        </td>
        <td>
          {row.level != 0 && (
            <ActionIcon onClick={() => removeRow(row)}>
              <IconTrash size={15} />
            </ActionIcon>
          )}
          {""}
        </td>
      </tr>
    );
  };

  const tableRows = displayRows.map((row) => (
    <OverrideLevelRow key={row.index} row={row} />
  ));

  const getTotalPercentage = () => {
    return overrideTable.reduce((a, c) => a + c.percentage ?? 0, 0);
  };

  const commitRows = (rows) => {
    mainForm.setFieldValue(rowsPath, renumberOverridingRows(rows));
  };

  const removeRow = (row) => {
    commitRows(displayRows.filter((item) => item.index !== row.index));
  };

  const updateFormValue = () => {
    commitRows(displayRows);
  };

  const addRow = () => {
    commitRows([
      ...overrideTable,
      {
        index: Number.MAX_SAFE_INTEGER,
        level: 1,
        levelType: "upper",
        userGroup: null,
        user: null,
        percentage: 0,
        isRemainLevel: false,
      },
    ]);
  };

  const totalPercent = getTotalPercentage();

  const tableSummaryRow = (
    <tr>
      <td></td>
      <td>
        {
          totalPercent != 100 && !allowSkipProviderRate && (
            <Text color="red"> {t("Total percentage is not 100% !")}</Text>
          ) // t("Total percentage must be 100%")
        }
        {!hasRemainLevel() && (
          <Text color="red"> {t("Please select a remain level!")}</Text>
        )}
      </td>
      <td></td>
      <td></td>

      <td>{formatter.percentage(totalPercent)} </td>
      <td></td>
      <td></td>
    </tr>
  );
  return (
    <>
      {/* <ReactJson
        src={mainForm.values.specialTables[specialTableIndex]}
        name="Table"
        style={{ background: "white" }}
        collapsed
      ></ReactJson> */}
      {isSpecial && (
        <Group
          mb="xl"
          align="flex-end"
          spacing="md"
          sx={{ width: "100%", flexWrap: "wrap" }}
        >
          <TextInput
            label="Name"
            size={"xs"}
            sx={{ width: 180, flex: "0 0 180px" }}
            value={_.get(
              mainForm.values,
              `specialTables.${specialTableIndex}.name`
            )}
            onChange={(e) =>
              mainForm.setFieldValue(
                `specialTables.${specialTableIndex}.name`,
                e.target.value
              )
            }
            placeholder="GI Year 1"
          />

          <Box sx={{ flex: "1 1 220px", minWidth: 220, maxWidth: "100%" }}>
          <RemoteSelect2
            label={t("Product Category")}
            apiEntity="productCategory"
            placeholder={t("Search")}
            valueField="_id"
            labelField="name"
            allowClear={true}
            labelRender={(data) => `${data.name} ${data.cname}`}
            value={_.get(
              mainForm.values,
              `specialTables.${specialTableIndex}.productCategory`
            )}
            onDataChange={(val) => {
              console.log(val);
              mainForm.setFieldValue(
                `specialTables.${specialTableIndex}.productCategory`,
                val
              );
            }}
            size={"xs"}
            pageSize={200}
            searchFields={["name", "code", "cname"]}
            clearable={true}
            sort={{
              by: "name",
              order: "asc",
            }}
          />
          </Box>
          <NumberInput
            sx={{ width: 150, flex: "0 0 150px" }}
            label={
              <FieldLabelGuide label={t("From Year")} title={t("From Year")}>
                {t(
                  "The first commission year this variation applies. Year 1 is the first year of the order."
                )}
              </FieldLabelGuide>
            }
            withAsterisk
            size="xs"
            min={1}
            max={100}
            placeholder="2"
            error={
              !Number.isFinite(
                Number(
                  _.get(
                    mainForm.values,
                    `specialTables.${specialTableIndex}.fromYear`
                  )
                )
              )
                ? t("From Year is required")
                : undefined
            }
            value={
              _.get(
                mainForm.values,
                `specialTables.${specialTableIndex}.fromYear`
              ) ?? ""
            }
            onChange={(val) =>
              mainForm.setFieldValue(
                `specialTables.${specialTableIndex}.fromYear`,
                val === "" ? null : val
              )
            }
          />
          <NumberInput
            sx={{ width: 170, flex: "0 0 170px" }}
            label={
              <FieldLabelGuide label={t("To Year")} title={t("To Year")}>
                {t(
                  "Leave To Year empty to apply this variation to every year from From Year onward."
                )}
              </FieldLabelGuide>
            }
            size="xs"
            min={1}
            max={100}
            placeholder={t("All later years")}
            value={
              _.get(
                mainForm.values,
                `specialTables.${specialTableIndex}.toYear`
              ) ?? ""
            }
            onChange={(val) =>
              mainForm.setFieldValue(
                `specialTables.${specialTableIndex}.toYear`,
                val === "" ? null : val
              )
            }
          />
        </Group>
      )}
      <Table>
        <thead> {head}</thead>
        <tbody>
          {tableRows}
          {tableSummaryRow}
        </tbody>
      </Table>

      <Button size={"xs"} mt={50} onClick={addRow}>
        {t("Add Level")}
      </Button>
    </>
  );
};

export default OverridingTable;
